import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table as STable,
  Loader,
  Message,
  Pagination,
  Dropdown,
  Input,
  Grid,
  GridColumn,
  Icon,
  Label,
} from 'semantic-ui-react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { getNotificationLogs } from '~/api/admin';
import '~/features/admin/companies/styles.scss';

type Recipient = {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  readAt: string | null;
  emailSentAt: string | null;
};

type NotificationError = {
  id: number;
  errorMessage: string;
  errorStack: string;
  errorTimestamp: string;
};

type NotificationRow = {
  id: number;
  type: string;
  message: string;
  emailMessage?: string | null;
  metadata?: Record<string, unknown> | null;
  isVisible: boolean;
  createdAt: string;
  conversationId?: number | null;
  useNotificationResolver?: boolean;
  project?: { id: number; name: string; parentId?: number | null } | null;
  portfolio?: { id: number; name: string; parentId?: number | null } | null;
  installer?: { id: number; company?: { id: number; name: string } | null } | null;
  investor?: { id: number; company?: { id: number; name: string } | null } | null;
  recipients: Recipient[];
  recipientCount: number;
  readCount: number;
  emailSentCount: number;
  errors: NotificationError[];
  errorCount: number;
};

const PAGE_SIZE = 50;
const colStyle = { padding: '1rem 0 0 1rem' };
const controlFontSize = { fontSize: '.9rem' };

const VISIBILITY_OPTIONS = [
  { key: 'visible', value: 'true', text: 'Visible only' },
  { key: 'hidden', value: 'false', text: 'Hidden only' },
];

const ERROR_OPTIONS = [
  { key: 'has', value: 'true', text: 'With errors' },
  { key: 'none', value: 'false', text: 'No errors' },
];

function truncateForMeta(s: string, max = 56): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}...`;
}

function parseParamsFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return {
    type: params.get('type') || '',
    projectName: params.get('projectName') || '',
    portfolioName: params.get('portfolioName') || '',
    recipientName: params.get('recipientName') || '',
    isVisible: params.get('isVisible') || '',
    hasError: params.get('hasError') || '',
    page: Math.max(1, parseInt(params.get('page') || '1', 10)),
    sortDirection: (params.get('sortDirection') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc',
  };
}

function buildSearchFromState(state: {
  typeFilter: string;
  projectFilter: string;
  portfolioFilter: string;
  recipientFilter: string;
  visibilityFilter: string;
  errorFilter: string;
  currentPage: number;
  sortDirection: 'asc' | 'desc';
}) {
  const p = new URLSearchParams();
  if (state.typeFilter.trim()) p.set('type', state.typeFilter.trim());
  if (state.projectFilter.trim()) p.set('projectName', state.projectFilter.trim());
  if (state.portfolioFilter.trim()) p.set('portfolioName', state.portfolioFilter.trim());
  if (state.recipientFilter.trim()) p.set('recipientName', state.recipientFilter.trim());
  if (state.visibilityFilter) p.set('isVisible', state.visibilityFilter);
  if (state.errorFilter) p.set('hasError', state.errorFilter);
  p.set('page', String(state.currentPage));
  p.set('sortDirection', state.sortDirection);
  return p.toString();
}

function formatDate(s: string | null | undefined) {
  if (!s) return '';
  return new Date(s).toLocaleString();
}

function ExpandedRow({ notification }: { notification: NotificationRow }) {
  return (
    <STable.Row>
      <STable.Cell colSpan={8} style={{ background: '#fafbfc' }}>
        <div style={{ padding: '0.75rem 1rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <strong>Message</strong>
            <div
              style={{
                marginTop: '0.25rem',
                whiteSpace: 'pre-wrap',
                background: '#fff',
                padding: '0.5rem',
                border: '1px solid #eee',
                borderRadius: 4,
              }}
            >
              {notification.message}
            </div>
          </div>

          {notification.emailMessage && (
            <div style={{ marginBottom: '1rem' }}>
              <strong>Email message</strong>
              <div
                style={{
                  marginTop: '0.25rem',
                  whiteSpace: 'pre-wrap',
                  background: '#fff',
                  padding: '0.5rem',
                  border: '1px solid #eee',
                  borderRadius: 4,
                }}
              >
                {notification.emailMessage}
              </div>
            </div>
          )}

          {notification.metadata && Object.keys(notification.metadata).length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <strong>Metadata</strong>
              <pre
                style={{
                  marginTop: '0.25rem',
                  background: '#fff',
                  padding: '0.5rem',
                  border: '1px solid #eee',
                  borderRadius: 4,
                  overflowX: 'auto',
                }}
              >
                {JSON.stringify(notification.metadata, null, 2)}
              </pre>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <strong>
              Recipients ({notification.recipientCount}) — read {notification.readCount}, emailed{' '}
              {notification.emailSentCount}
            </strong>
            {notification.recipients.length === 0 ? (
              <div style={{ color: '#888', marginTop: '0.25rem' }}>No recipients.</div>
            ) : (
              <STable compact size="small" style={{ marginTop: '0.5rem' }}>
                <STable.Header>
                  <STable.Row>
                    <STable.HeaderCell>User</STable.HeaderCell>
                    <STable.HeaderCell>Email</STable.HeaderCell>
                    <STable.HeaderCell>Read at</STable.HeaderCell>
                    <STable.HeaderCell>Email sent at</STable.HeaderCell>
                  </STable.Row>
                </STable.Header>
                <STable.Body>
                  {notification.recipients.map((r) => (
                    <STable.Row key={r.userId}>
                      <STable.Cell>{r.userName ?? `(user #${r.userId})`}</STable.Cell>
                      <STable.Cell>{r.userEmail ?? ''}</STable.Cell>
                      <STable.Cell>{formatDate(r.readAt)}</STable.Cell>
                      <STable.Cell>{formatDate(r.emailSentAt)}</STable.Cell>
                    </STable.Row>
                  ))}
                </STable.Body>
              </STable>
            )}
          </div>

          {notification.errors.length > 0 && (
            <div>
              <strong style={{ color: '#b03060' }}>
                Errors ({notification.errorCount})
              </strong>
              {notification.errors.map((e) => (
                <div
                  key={e.id}
                  style={{
                    marginTop: '0.5rem',
                    background: '#fff',
                    padding: '0.5rem',
                    border: '1px solid #f0c0c0',
                    borderRadius: 4,
                  }}
                >
                  <div style={{ fontSize: '.85rem', color: '#666' }}>
                    {formatDate(e.errorTimestamp)}
                  </div>
                  <div style={{ fontWeight: 600, marginTop: '0.25rem' }}>{e.errorMessage}</div>
                  {e.errorStack && (
                    <pre
                      style={{
                        marginTop: '0.25rem',
                        fontSize: '.8rem',
                        whiteSpace: 'pre-wrap',
                        color: '#444',
                      }}
                    >
                      {e.errorStack}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </STable.Cell>
    </STable.Row>
  );
}

export default function NotificationLogTable() {
  const location = useLocation();
  const history = useHistory();
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [typeFilter, setTypeFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [errorFilter, setErrorFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const hasSyncedFromUrl = useRef(false);

  useEffect(() => {
    const parsed = parseParamsFromSearch(location.search);
    setTypeFilter(parsed.type);
    setProjectFilter(parsed.projectName);
    setPortfolioFilter(parsed.portfolioName);
    setRecipientFilter(parsed.recipientName);
    setVisibilityFilter(parsed.isVisible);
    setErrorFilter(parsed.hasError);
    setCurrentPage(parsed.page);
    setSortDirection(parsed.sortDirection);
  }, [location.search]);

  useEffect(() => {
    if (!hasSyncedFromUrl.current) {
      hasSyncedFromUrl.current = true;
      return;
    }
    const search = buildSearchFromState({
      typeFilter,
      projectFilter,
      portfolioFilter,
      recipientFilter,
      visibilityFilter,
      errorFilter,
      currentPage,
      sortDirection,
    });
    const currentParsed = parseParamsFromSearch(location.search);
    const urlMatchesState =
      currentParsed.type === typeFilter &&
      currentParsed.projectName === projectFilter &&
      currentParsed.portfolioName === portfolioFilter &&
      currentParsed.recipientName === recipientFilter &&
      currentParsed.isVisible === visibilityFilter &&
      currentParsed.hasError === errorFilter &&
      currentParsed.page === currentPage &&
      currentParsed.sortDirection === sortDirection;
    if (!urlMatchesState) {
      history.replace(`${location.pathname}?${search}`);
    }
  }, [
    location.pathname,
    typeFilter,
    projectFilter,
    portfolioFilter,
    recipientFilter,
    visibilityFilter,
    errorFilter,
    currentPage,
    sortDirection,
  ]);

  useEffect(() => {
    document.title = 'Notification Log | Conductor Solar';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const offset = (currentPage - 1) * PAGE_SIZE;
        const params: any = { limit: PAGE_SIZE, offset, sortDirection };
        if (typeFilter.trim()) params.type = typeFilter.trim();
        if (projectFilter.trim()) params.projectName = projectFilter.trim();
        if (portfolioFilter.trim()) params.portfolioName = portfolioFilter.trim();
        if (recipientFilter.trim()) params.recipientName = recipientFilter.trim();
        if (visibilityFilter) params.isVisible = visibilityFilter;
        if (errorFilter) params.hasError = errorFilter;

        const response = await getNotificationLogs(params);
        setNotifications(response.notifications || []);
        setTotal(response.total || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to fetch notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    currentPage,
    sortDirection,
    typeFilter,
    projectFilter,
    portfolioFilter,
    recipientFilter,
    visibilityFilter,
    errorFilter,
  ]);

  const handleToggleSortByDate = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const getSortIcon = () => (sortDirection === 'asc' ? ' ▲' : ' ▼');

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasFilters = useMemo(
    () =>
      typeFilter.trim().length > 0 ||
      projectFilter.trim().length > 0 ||
      portfolioFilter.trim().length > 0 ||
      recipientFilter.trim().length > 0 ||
      visibilityFilter !== '' ||
      errorFilter !== '',
    [typeFilter, projectFilter, portfolioFilter, recipientFilter, visibilityFilter, errorFilter]
  );

  const clearAllFilters = () => {
    setTypeFilter('');
    setProjectFilter('');
    setPortfolioFilter('');
    setRecipientFilter('');
    setVisibilityFilter('');
    setErrorFilter('');
    setCurrentPage(1);
  };

  const activeFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (typeFilter.trim()) parts.push(`Type contains "${truncateForMeta(typeFilter.trim())}"`);
    if (projectFilter.trim()) parts.push(`Project contains "${truncateForMeta(projectFilter.trim())}"`);
    if (portfolioFilter.trim())
      parts.push(`Portfolio contains "${truncateForMeta(portfolioFilter.trim())}"`);
    if (recipientFilter.trim())
      parts.push(`Recipient contains "${truncateForMeta(recipientFilter.trim())}"`);
    if (visibilityFilter === 'true') parts.push('Visible only');
    if (visibilityFilter === 'false') parts.push('Hidden only');
    if (errorFilter === 'true') parts.push('With errors');
    if (errorFilter === 'false') parts.push('No errors');
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [typeFilter, projectFilter, portfolioFilter, recipientFilter, visibilityFilter, errorFilter]);

  return (
    <div>
      <div className="card">
        <div style={{ margin: '1rem 0 2rem', paddingRight: '1rem' }}>
          <Grid stackable>
            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder="Type contains..."
                value={typeFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setTypeFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>
            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder="Project name contains..."
                value={projectFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setProjectFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>
            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder="Portfolio name contains..."
                value={portfolioFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setPortfolioFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>
            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder="Recipient name or email..."
                value={recipientFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setRecipientFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>
            <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
              <Dropdown
                placeholder="Visibility..."
                fluid
                selection
                clearable
                options={VISIBILITY_OPTIONS}
                value={visibilityFilter}
                onChange={(_, { value }) => {
                  setCurrentPage(1);
                  setVisibilityFilter((value as string) || '');
                }}
                style={controlFontSize}
                selectOnBlur={false}
              />
            </GridColumn>
            <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
              <Dropdown
                placeholder="Errors..."
                fluid
                selection
                clearable
                options={ERROR_OPTIONS}
                value={errorFilter}
                onChange={(_, { value }) => {
                  setCurrentPage(1);
                  setErrorFilter((value as string) || '');
                }}
                style={controlFontSize}
                selectOnBlur={false}
              />
            </GridColumn>
          </Grid>
        </div>
      </div>

      {error && <Message negative>{error}</Message>}
      {loading && <Loader active inline="centered" />}

      {!loading && !error && notifications.length === 0 && (
        <Message info>No notifications found matching your filters.</Message>
      )}

      {!loading && !error && notifications.length > 0 && (
        <div className="card">
          <div className="admin-companies-search-meta" style={{ marginBottom: '0.5rem' }}>
            Showing {(currentPage - 1) * PAGE_SIZE + 1} -{' '}
            {Math.min(currentPage * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + notifications.length)}{' '}
            of {total} notifications
            {hasFilters && (
              <>
                {' - '}
                <button
                  type="button"
                  className="admin-companies-clear-link"
                  onClick={clearAllFilters}
                >
                  Clear filters
                </button>
              </>
            )}
            {activeFilterDescription && (
              <>
                <br />
                {activeFilterDescription}
              </>
            )}
          </div>

          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <STable striped>
              <STable.Header>
                <STable.Row>
                  <STable.HeaderCell style={{ width: '2rem' }} />
                  <STable.HeaderCell
                    onClick={handleToggleSortByDate}
                    style={{ cursor: 'pointer' }}
                  >
                    Date{getSortIcon()}
                  </STable.HeaderCell>
                  <STable.HeaderCell>Type</STable.HeaderCell>
                  <STable.HeaderCell>Project</STable.HeaderCell>
                  <STable.HeaderCell>Portfolio</STable.HeaderCell>
                  <STable.HeaderCell>Recipients</STable.HeaderCell>
                  <STable.HeaderCell>Visible</STable.HeaderCell>
                  <STable.HeaderCell>Errors</STable.HeaderCell>
                </STable.Row>
              </STable.Header>
              <STable.Body>
                {notifications.map((n) => {
                  const isOpen = expanded.has(n.id);
                  return (
                    <React.Fragment key={n.id}>
                      <STable.Row>
                        <STable.Cell>
                          <Icon
                            link
                            name={isOpen ? 'caret down' : 'caret right'}
                            onClick={() => toggleExpand(n.id)}
                          />
                        </STable.Cell>
                        <STable.Cell style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(n.createdAt)}
                        </STable.Cell>
                        <STable.Cell>{n.type}</STable.Cell>
                        <STable.Cell>
                          {n.project?.id ? (
                            <Link to={`/project/${n.project.parentId ?? n.project.id}`}>
                              {n.project.name}
                            </Link>
                          ) : (
                            ''
                          )}
                        </STable.Cell>
                        <STable.Cell>
                          {n.portfolio?.id ? (
                            <Link to={`/portfolio/${n.portfolio.parentId ?? n.portfolio.id}`}>
                              {n.portfolio.name}
                            </Link>
                          ) : (
                            ''
                          )}
                        </STable.Cell>
                        <STable.Cell>{n.recipientCount}</STable.Cell>
                        <STable.Cell>
                          {n.isVisible ? (
                            'Yes'
                          ) : (
                            <Label size="mini" color="grey">
                              Hidden
                            </Label>
                          )}
                        </STable.Cell>
                        <STable.Cell>
                          {n.errorCount > 0 ? (
                            <Label size="mini" color="red">
                              {n.errorCount}
                            </Label>
                          ) : (
                            ''
                          )}
                        </STable.Cell>
                      </STable.Row>
                      {isOpen && <ExpandedRow notification={n} />}
                    </React.Fragment>
                  );
                })}
              </STable.Body>
            </STable>
          </div>

          <div
            style={{
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {totalPages > 1 && (
              <Pagination
                activePage={currentPage}
                onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
                totalPages={totalPages}
                siblingRange={1}
                boundaryRange={1}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: '←', icon: false }}
                nextItem={{ content: '→', icon: false }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
